'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import {
  BriefcaseIcon,
  CheckCircle,
  CheckCircle2,
  EditIcon,
  FolderLockIcon,
  GlobeIcon,
  Mail,
  PhoneIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { UserAvatar } from '@/components/partials'
import { UpdateProfileDialog } from '@/components/partials/profile'
import { useMeStore } from '@/store'
import { formatDateTime, formatDate } from '@/lib/utils'

const ProfileSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-16 w-16 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-40" />
        </div>
      </div>
      <Skeleton className="h-9 w-32" />
    </div>
    <Card>
      <CardContent className="space-y-6 pt-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
)

const Profile = () => {
  const t = useTranslations()
  const locale = useLocale()
  const { meState } = useMeStore()
  const user = meState.response
  const [editOpen, setEditOpen] = useState(false)

  if (!user) {
    return <ProfileSkeleton />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <UserAvatar user={user} size="lg" showOnlineStatus />
          <div>
            <h1 className="text-2xl font-bold">{user.fullName}</h1>
            <div className="text-muted-foreground flex items-center gap-1 text-sm">
              <Mail className="h-3 w-3" />
              <span>{user.email}</span>
            </div>
            {user.emailVerifiedAt && (
              <div className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle className="h-3 w-3" />
                {t('email_verified')}: {formatDateTime(user.emailVerifiedAt, locale)}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button onClick={() => setEditOpen(true)}>
            <EditIcon className="h-4 w-4" />
            <span className="hidden lg:flex">{t('edit_profile')}</span>
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card>
        <CardContent className="space-y-6 pt-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t('status')}:</span>
              <Badge
                variant="outline"
                className={
                  user.online
                    ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-300'
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                }>
                {t(user.online ? 'online' : 'offline')}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t('gender')}:</span>
              <Badge variant="outline">{t(user.gender)}</Badge>
            </div>
            {user.company && (
              <div className="flex items-center gap-2 text-sm">
                <BriefcaseIcon className="text-muted-foreground h-4 w-4" />
                <span>{user.company}</span>
              </div>
            )}
            {user.phone && (
              <div className="flex items-center gap-2 text-sm">
                <PhoneIcon className="text-muted-foreground h-4 w-4" />
                <span>{user.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t('birth_date')}:</span>
              <span>{formatDate(user.birthDate, locale)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <GlobeIcon className="text-muted-foreground h-4 w-4" />
              <span className="font-mono">{user.timezone}</span>
            </div>
            {user.currencyCode && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('currency')}:</span>
                <span className="font-mono">{user.currencyCode}</span>
              </div>
            )}
            <div className="flex items-start gap-2 text-sm">
              <span className="text-muted-foreground shrink-0">{t('roles')}:</span>
              <div className="flex flex-wrap gap-1">
                {user.roles.map(role => (
                  <Badge key={role} variant="outline" className="text-xs">
                    {t(role)}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <span className="text-muted-foreground shrink-0">{t('groups')}:</span>
              <div className="flex flex-wrap gap-1">
                {user.groups.length > 0 ? (
                  user.groups.map(group => (
                    <Link href={`/users/groups/${group.id}`} key={group.id}>
                      <Badge variant="outline" className="text-xs">
                        {group.name}
                      </Badge>
                    </Link>
                  ))
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs">{t('created_at')}</p>
              <p className="text-sm">{formatDateTime(user.createdAt, locale)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs">{t('updated_at')}</p>
              <p className="text-sm">{formatDateTime(user.updatedAt, locale)}</p>
            </div>
            {user.approvedAt && (
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">{t('approved_at')}</p>
                <p className="text-sm">{formatDateTime(user.approvedAt, locale)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Permissions Card */}
      {user.permissions && user.permissions.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center space-x-2 pb-4">
            <FolderLockIcon className="h-5 w-5" />
            <h2 className="text-lg font-semibold">{t('permissions')}</h2>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {user.permissions.map((permission: any) => (
                <div key={permission.id} className="rounded-lg border p-4">
                  <div className="mb-3 font-semibold">{t(permission.name.toLowerCase())}</div>
                  <Separator className="mb-3" />
                  <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 xl:grid-cols-5">
                    {permission.actions.map((action: any) => (
                      <div key={action.id} className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{t(`permission_actions_${action.name.toLowerCase()}`)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <UpdateProfileDialog open={editOpen} onOpenChange={setEditOpen} />
    </div>
  )
}

export default Profile
